import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function CourseStructure() {
  const phases = [
    {
      number: '01',
      title: 'Understand',
      subtitle: 'Country and cultural protocols',
      description: 'Build shared understanding of Country as a living cultural system and the protocols that guide respectful practice in NSW projects.',
      icon: 'ri-eye-line',
      color: 'bg-[#E48523]',
      keywords: ['Country', 'Cultural protocols'],
    },
    {
      number: '02',
      title: 'Engage',
      subtitle: 'Respectful engagement and community involvement',
      description: 'Learn culturally safe engagement with Traditional Custodians, including how to determine appropriate levels of community involvement.',
      icon: 'ri-shake-hands-line',
      color: 'bg-[#BA3D03]',
      keywords: ['Traditional Custodians', 'Involvement spectrum'],
    },
    {
      number: '03',
      title: 'Integrate',
      subtitle: 'Policy to delivery decisions',
      description: 'Translate Better Placed and Connecting with Country into planning, design and delivery decisions across the project lifecycle.',
      icon: 'ri-git-merge-line',
      color: 'bg-[#3D1203]',
      keywords: ['Better Placed', 'Connecting with Country'],
    },
    {
      number: '04',
      title: 'Sustain',
      subtitle: 'ICIP and long-term stewardship',
      description: 'Embed ICIP-aware practice and long-term stewardship so Country-led commitments continue beyond project completion.',
      icon: 'ri-seedling-line',
      color: 'bg-[#E48523]',
      keywords: ['ICIP', 'Stewardship'],
    },
  ];

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section id="course-structure" className="py-24 bg-[#FFF9F5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 transition-all duration-700 ease-out ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center px-4 py-2 bg-[#E48523]/10 text-[#BA3D03] rounded-full text-sm font-semibold mb-6">
            4-Phase Learning Journey
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#3D1203] mb-6">
            How the Course is Structured
          </h2>
          <p className="text-lg text-[#3D1203]/70 max-w-3xl mx-auto">
            A structured pathway from Country and cultural protocols through to culturally safe engagement, ICIP-aware practice, and practical delivery aligned with Better Placed and Connecting with Country.
          </p>
        </div>

        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {phases.map((phase, index) => (
            <div
              key={index}
              className={`bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-all duration-500 border border-[#E8C580]/30 ${
                gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: gridVisible ? `${index * 120}ms` : '0ms' }}
            >
              <div className={`w-14 h-14 ${phase.color} rounded-lg flex items-center justify-center mb-6`}>
                <i className={`${phase.icon} text-2xl text-white`}></i>
              </div>
              <div className="text-sm font-bold text-[#BA3D03] mb-2">Phase {phase.number}</div>
              <h3 className="text-2xl font-bold text-[#3D1203] mb-2">{phase.title}</h3>
              <div className="text-sm font-semibold text-[#E48523] mb-4">{phase.subtitle}</div>
              <p className="text-[#3D1203]/70 leading-relaxed">{phase.description}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                {phase.keywords.map((kw, i) => (
                  <span key={i} className="text-xs text-[#BA3D03]/70 bg-[#E48523]/10 px-2 py-0.5 rounded-full">
                    {kw}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
